import React, { useState, useEffect, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import BookmarksOutlinedIcon from "@mui/icons-material/BookmarksOutlined";
import { getUser } from "../graphql/queries";
import { updateQuestion, createUser } from "../graphql/mutations";
import SavedQuestion from "./SavedQuestion";
import UserContext from "./UserContext";
import ProgressStatistics from "./ProgressStatitstics";
import HorizontalScrollCalendar from "./HorizontalScrollCalendar";
import ProfileCard from "./ProfileCard";

const INK = "#0f1121";
const INK_SOFT = "rgba(15, 17, 33, 0.62)";
const INK_FAINT = "rgba(15, 17, 33, 0.42)";
const INK_HAIRLINE = "rgba(15, 17, 33, 0.08)";
const ACCENT = "#3854e8";
const SURFACE = "#ffffff";
const CANVAS = "#f6f6f4";

const MONO =
  "'JetBrains Mono', 'Söhne Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = "'sohne-var', 'Helvetica Neue', 'Arial', sans-serif";

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.04 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 110, damping: 22 }
  }
};

const SkeletonLine = ({ w = "100%", h = 12, mb = 1 }) => (
  <Box
    sx={{
      width: w,
      height: h,
      borderRadius: "6px",
      mb,
      background:
        "linear-gradient(90deg, rgba(15, 17, 33, 0.05) 0%, rgba(15, 17, 33, 0.11) 50%, rgba(15, 17, 33, 0.05) 100%)",
      backgroundSize: "200% 100%",
      animation: "skeletonShimmer 1.4s ease-in-out infinite",
      "@keyframes skeletonShimmer": {
        "0%": { backgroundPosition: "200% 0" },
        "100%": { backgroundPosition: "-200% 0" }
      }
    }}
  />
);

export default function Profile() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [questions, setQuestions] = useState([]);
  const [conversationText, setConversationText] = useState([]);
  const [blobURLs, setBlobURLs] = useState([]);
  const [audioKeys, setAudioKeys] = useState([]);
  const [responseTexts, setResponseTexts] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subscriptionID, setSubscriptionID] = useState("");
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [questionsToday, setQuestionsToday] = useState(0);
  const { user, subscriptionType, setSubscriptionType } = useContext(
    UserContext
  );
  const [userId] = useState(user ? user.username : null);

  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const handleBlobURLChange = (index, blobURL) => {
    const newBlobURLs = [...blobURLs];
    newBlobURLs[index] = blobURL;
    setBlobURLs(newBlobURLs);
  };
  const handleAudioKeyChange = (index, audioKey) => {
    const newAudioKeys = [...audioKeys];
    newAudioKeys[index] = audioKey;
    setAudioKeys(newAudioKeys);
  };
  const handleConversationTextChange = newItems => {
    setConversationText([...conversationText, ...newItems]);
  };
  const handleResponseTextChange = (index, responseText) => {
    const newResponseTexts = [...responseTexts];
    newResponseTexts[index] = responseText;
    setResponseTexts(newResponseTexts);
  };

  const handleFeedbackChange = async (index, feedback, responseText, questionID) => {
    try {
      const updatedQuestion = await API.graphql(
        graphqlOperation(updateQuestion, {
          input: {
            id: questionID,
            responseText: responseText,
            feedback: feedback
          }
        })
      );
      const newFeedbacks = [...feedbacks];
      newFeedbacks[index] = updatedQuestion.data.updateQuestion.feedback;
      setFeedbacks(newFeedbacks);
      const newResponseTexts = [...responseTexts];
      newResponseTexts[index] = updatedQuestion.data.updateQuestion.responseText;
      setResponseTexts(newResponseTexts);
    } catch (error) {
      console.log(error);
    }
  };

  const removeQuestion = questionID => {
    setQuestions(questions.filter(question => question.id !== questionID));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { data, errors } = await API.graphql(
          graphqlOperation(getUser, { id: user.username })
        );
        if (errors) console.log("Errors:", errors);

        if (!data.getUser) {
          await API.graphql(
            graphqlOperation(createUser, {
              input: {
                id: user.username,
                username: user.username,
                name: user.attributes.name,
                dailyQueries: 0,
                subscriptionType: "FREE"
              }
            })
          );
          setSubscriptionType("FREE");
        } else {
          setEmail(user.attributes.email);
          setName(user.attributes.name);
          setSubscriptionType(data.getUser.subscriptionType);
          setSubscriptionID(data.getUser.stripeSubscriptionID);
          const today = new Date().toISOString().split("T")[0];
          let totalCount = 0;
          (data.getUser.dailyAnsweredQuestions.items || []).forEach(d => {
            totalCount += d.answeredQuestionsCount;
          });
          const todayItem = (data.getUser.dailyAnsweredQuestions.items || []).find(
            d => d.date === today
          );
          setTotalQuestions(totalCount);
          setQuestionsToday(todayItem ? todayItem.answeredQuestionsCount : 0);
        }

        const qs = data.getUser ? data.getUser.questions.items : [];
        setQuestions(qs);
        const newResponseTexts = [];
        const newFeedbacks = [];
        const newConversation = [];
        qs.forEach((question, index) => {
          newResponseTexts[index] = question.responseText;
          newFeedbacks[index] = question.feedback;
          newConversation[index] = question.conversation;
        });
        setResponseTexts(newResponseTexts);
        setFeedbacks(newFeedbacks);
        setConversationText(newConversation);
      } catch (error) {
        console.log("error fetching user data", error);
      } finally {
        setLoading(false);
      }
    };
    if (user) fetchUserData();
    else setLoading(false);
  }, [user]);

  const questionCards = questions.map((question, index) => (
    <Box
      key={question.id}
      sx={{
        position: "relative",
        backgroundColor: "rgba(15, 17, 33, 0.02)",
        border: `1px solid ${INK_HAIRLINE}`,
        borderRadius: "18px",
        p: { xs: 2.5, sm: 3 },
        mt: index === 0 ? 0 : 2.5
      }}
    >
      <Typography
        sx={{
          fontFamily: MONO,
          fontSize: "0.66rem",
          fontWeight: 500,
          letterSpacing: "0.18em",
          textTransform: "uppercase",
          color: INK_FAINT,
          mb: 1.5,
          fontVariantNumeric: "tabular-nums"
        }}
      >
        Saved · {String(index + 1).padStart(2, "0")}
      </Typography>
      <SavedQuestion
        question={question.questionText}
        questionID={question.id}
        removeQuestion={() => removeQuestion(question.id)}
        index={index}
        conversation={conversationText[index]}
        blobURL={blobURLs[index]}
        audioKey={audioKeys[index]}
        responseText={responseTexts[index]}
        feedback={feedbacks[index]}
        feedbackGeneratedAt={question.updatedAt || question.createdAt}
        onBlobURLChange={blobURL => handleBlobURLChange(index, blobURL)}
        onAudioKeyChange={audioKey => handleAudioKeyChange(index, audioKey)}
        onResponseTextChange={responseText =>
          handleResponseTextChange(index, responseText)
        }
        onConversationTextChange={handleConversationTextChange}
        onFeedbackChange={feedback =>
          handleFeedbackChange(
            index,
            feedback,
            responseTexts[index],
            question.id
          )
        }
      />
    </Box>
  ));

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        backgroundColor: CANVAS,
        minHeight: "100vh",
        textAlign: "left",
        py: { xs: 6, md: 9 },
        px: { xs: 2, sm: 3 }
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: 160,
          background:
            "linear-gradient(to bottom, #ffffff 0%, rgba(255, 255, 255, 0.6) 45%, rgba(255, 255, 255, 0) 100%)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          top: -220,
          right: -180,
          width: 620,
          height: 620,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 30% 30%, rgba(228, 222, 207, 0.7), transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          bottom: -260,
          left: -180,
          width: 540,
          height: 540,
          borderRadius: "50%",
          background:
            "radial-gradient(circle at 50% 50%, rgba(56, 84, 232, 0.07), transparent 70%)",
          filter: "blur(40px)",
          pointerEvents: "none",
          zIndex: 0
        }}
      />
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: "-25%",
          backgroundImage:
            "repeating-linear-gradient(135deg, rgba(15, 17, 33, 0.05) 0, rgba(15, 17, 33, 0.05) 1px, transparent 1px, transparent 22px)",
          animation: "profileStripesDrift 32s linear infinite",
          willChange: "transform",
          pointerEvents: "none",
          zIndex: 0,
          maskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.85), transparent 85%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at 50% 50%, rgba(0,0,0,0.85), transparent 85%)",
          "@keyframes profileStripesDrift": {
            "0%": { transform: "translate3d(0, 0, 0)" },
            "100%": { transform: "translate3d(-44px, -44px, 0)" }
          },
          "@media (prefers-reduced-motion: reduce)": { animation: "none" }
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="show"
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "minmax(280px, 5fr) 7fr" },
            gap: { xs: 4, md: 4 },
            alignItems: "start"
          }}
        >
          <Box
            component={motion.div}
            variants={itemVariants}
            sx={{
              position: { xs: "static", md: "sticky" },
              top: { md: 96 }
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 2.25
              }}
            >
              <Box
                component={motion.span}
                animate={{ opacity: [0.55, 1, 0.55] }}
                transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: ACCENT,
                  display: "inline-block"
                }}
              />
              <Typography
                sx={{
                  fontFamily: SANS,
                  fontSize: "0.74rem",
                  fontWeight: 600,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: INK_SOFT
                }}
              >
                Your studio
              </Typography>
            </Box>
            <Typography
              component={motion.h1}
              variants={itemVariants}
              sx={{
                fontFamily: SANS,
                fontWeight: 600,
                fontSize: { xs: "1.9rem", sm: "2.2rem", md: "2.4rem" },
                lineHeight: 1.05,
                letterSpacing: "-0.025em",
                color: INK,
                m: 0,
                mb: 3
              }}
            >
              Profile & progress.
            </Typography>
            <ProfileCard
              name={name}
              subscriptionType={subscriptionType}
              email={email}
              subscriptionID={subscriptionID}
            />
          </Box>

          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <Box component={motion.div} variants={itemVariants}>
              <ProgressStatistics
                totalQuestions={totalQuestions}
                questionsToday={questionsToday}
              />
            </Box>
            <Box component={motion.div} variants={itemVariants}>
              <HorizontalScrollCalendar
                month={currentMonth}
                year={currentYear}
                userId={userId}
              />
            </Box>

            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                backgroundColor: SURFACE,
                borderRadius: "24px",
                border: `1px solid ${INK_HAIRLINE}`,
                boxShadow:
                  "inset 0 1px 0 rgba(255, 255, 255, 0.6), 0 24px 60px -32px rgba(15, 17, 33, 0.18), 0 2px 6px -2px rgba(15, 17, 33, 0.05)",
                p: { xs: 3, sm: 4 },
                textAlign: "left"
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2.5,
                  flexWrap: "wrap"
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                  <BookmarksOutlinedIcon
                    sx={{ fontSize: 18, color: ACCENT }}
                  />
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: "0.7rem",
                      fontWeight: 500,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      color: INK_FAINT
                    }}
                  >
                    Saved questions
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontFamily: MONO,
                    fontSize: "0.7rem",
                    color: INK_FAINT,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "0.06em"
                  }}
                >
                  {String(questions.length).padStart(2, "0")} total
                </Typography>
              </Box>

              {loading ? (
                <Box>
                  <SkeletonLine w="68%" h={14} mb={1.5} />
                  <SkeletonLine w="92%" />
                  <SkeletonLine w="84%" />
                </Box>
              ) : questions.length > 0 ? (
                <Box>{questionCards}</Box>
              ) : (
                <Box
                  sx={{
                    py: 4,
                    px: 3,
                    border: `1px dashed ${INK_HAIRLINE}`,
                    borderRadius: "16px",
                    textAlign: "left"
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: SANS,
                      fontSize: "1rem",
                      fontWeight: 500,
                      color: INK,
                      mb: 0.75
                    }}
                  >
                    No saved questions yet.
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: SANS,
                      fontSize: "0.92rem",
                      color: INK_SOFT,
                      lineHeight: 1.5
                    }}
                  >
                    Hit the bookmark on any practice question and it'll land
                    here, ready to drill again with full feedback history.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
